import PageHeader from '../../components/PageHeader';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { Device, Telemetry } from '../../types';
import { deviceAPI, telemetryAPI } from '../../services/api';

import { formatDate, formatDateTime } from '../../utils/dateUtils';

const TelemetryPage: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [telemetry, setTelemetry] = useState<Telemetry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [devicesData, telemetryData] = await Promise.all([
        deviceAPI.getAllDevices(),
        telemetryAPI.getLatestTelemetry()
      ]);
      setDevices(devicesData.data);
      setTelemetry(telemetryData);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredDevices = devices.filter(device => {
    if (search) {
      const query = search.toLowerCase();
      return (
        device.make?.toLowerCase().includes(query) ||
        device.plateNumber?.toLowerCase().includes(query) ||
        device.imei?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const onlineCount = devices.filter(d => d.status === 'online').length;
  const offlineCount = devices.filter(d => d.status === 'offline').length;

  return (
    <Box>
      <PageHeader title="Telemetry" description="Monitor connected devices and review vehicle telemetry" icon="telemetry" />

      {/* Search and Controls */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Search devices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<SettingsIcon />}>
            Settings
          </Button>
        </Grid>
      </Grid>

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Devices
              </Typography>
              <Typography variant="h4">
                {devices.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Online Devices
              </Typography>
              <Typography variant="h4" color="success.main">
                {onlineCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Offline Devices
              </Typography>
              <Typography variant="h4" color="text.secondary">
                {offlineCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Telemetry Records
              </Typography>
              <Typography variant="h4" color="primary">
                {telemetry.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Device Management Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Device Management</Typography>
            <Stack direction="row" spacing={1}>
              <Chip label={`All (${devices.length})`} />
              <Chip color="success" label={`Online (${onlineCount})`} />
              <Chip color="default" label={`Offline (${offlineCount})`} />
            </Stack>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <Box sx={{ width: '100%', overflowX: 'auto' }}><Table>
              <TableHead>
                <TableRow>
                  <TableCell>Device</TableCell>
                  <TableCell>IMEI</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell>Speed</TableCell>
                  <TableCell>Battery</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDevices.map((device) => (
                  <TableRow key={device._id || device.imei} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {device.make || 'Device'} {device.modelYear || ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {device.plateNumber || 'No Plate'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{device.imei}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        color={device.status === 'online' ? 'success' : 'default'} 
                        label={device.status} 
                      />
                    </TableCell>
                    <TableCell>
                    {device.lastSeen ? formatDate(device.lastSeen) : 'Never'}
                    </TableCell>
                    <TableCell>{device.currentSpeed ?? 0} km/h</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        color={device.batteryLevel > 20 ? 'success' : 'warning'} 
                        label={`${device.batteryLevel ?? 0}%`} 
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Details">
                          <IconButton size="small" color="primary">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Device">
                          <IconButton size="small" color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Device">
                          <IconButton size="small" color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></Box>
          )}
        </CardContent>
      </Card>

      {/* Telemetry Data Overview */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Latest Telemetry Data
          </Typography>
          
          {telemetry.length > 0 ? (
            <Box sx={{ width: '100%', overflowX: 'auto' }}><Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Device</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Speed</TableCell>
                  <TableCell>Battery</TableCell>
                  <TableCell>Fuel</TableCell>
                  <TableCell>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {telemetry.slice(0, 10).map((t) => (
                  <TableRow key={t._id} hover>
                    <TableCell>{t.imei}</TableCell>
                    <TableCell>
                    {formatDateTime(t.timestamp)}
                    </TableCell>
                    <TableCell>{t.speed} km/h</TableCell>
                    <TableCell>{t.battery}%</TableCell>
                    <TableCell>{t.fuelLevel}%</TableCell>
                    <TableCell>
                      {t.location?.coordinates?.length === 2 
                        ? `${t.location.coordinates[1].toFixed(3)}, ${t.location.coordinates[0].toFixed(3)}`
                        : '—'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></Box>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No telemetry data available
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TelemetryPage;
